import DynamicForm from '@/designs/new/components/forms/DynamicForm'
import React, { useEffect, useState } from 'react'
import {
  Field,
  FieldOption,
  FormData,
} from '@/designs/new/components/forms/types'
import { toast } from 'sonner'
import { Case } from './CaseDetails'

interface Props {
  isViewOnly?: boolean
  casesObj?: Case
}

const CaseDetailsForm: React.FC<Props> = ({ isViewOnly, casesObj }) => {
  const [serviceProviders, setServiceProviders] = useState<FieldOption[]>([])
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({})

  const formRef = React.useRef<{ submit: () => void } | null>(null)

  // console.log('casesObj:', casesObj)

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }

  // Simulated API call for dependent dropdown
  const fetchProviders = async (
    serviceType: string,
  ): Promise<FieldOption[]> => {
    await new Promise(resolve => setTimeout(resolve, 500))

    const providersByService: Record<string, FieldOption[]> = {
      'Hardware Support': [
        { value: 'techsolutions', label: 'TechSolutions Inc.' },
        { value: 'hardware-pro', label: 'Hardware Pro' },
      ],
      'Software Support': [
        { value: 'soft-experts', label: 'Software Experts' },
        { value: 'code-masters', label: 'Code Masters' },
      ],
    }

    return providersByService[serviceType] || []
  }

  const generalFields: Field[] = [
    {
      name: 'description',
      type: 'textarea',
      label: 'Description',
      placeholder: 'User is reporting a critical system failure...',
      required: true,
      rows: 3,
      rules: { required: 'Description is required' },
    },
    {
      type: 'row',
      fields: [
        {
          name: 'status',
          type: 'select',
          label: 'Status',
          required: true,
          options: [],
          rules: { required: 'Status is required' },
        },
        {
          name: 'statusReason',
          type: 'select',
          label: 'Status Reason',
          options: [],
        },
      ],
    },
    {
      type: 'row',
      fields: [
        {
          name: 'dateOpened',
          type: 'datetime-local',
          label: 'Date/Time (Open)',
          required: true,
          rules: { required: 'Open date is required' },
        },
        {
          name: 'dateClosed',
          type: 'datetime-local',
          label: 'Date/Time (Closed)',
        },
      ],
    },
    {
      type: 'row',
      fields: [
        {
          name: 'primaryService',
          type: 'select',
          label: 'Primary Service',
          required: true,
          placeholder: 'Select service',
          options: [],
          rules: { required: 'Primary service is required' },
          onChange: async (value: string, setValue) => {
            setValue('provider', '')
            const providers = await fetchProviders(value)
            setServiceProviders(providers)
          },
        },
        {
          name: 'provider',
          type: 'select',
          label: 'Provider',
          required: true,
          placeholder: 'Select provider',
          options: serviceProviders,
          dependsOn: 'primaryService',
          rules: { required: 'Provider is required' },
        },
      ],
    },
  ]

  const contactFields: Field[] = [
    {
      type: 'row',
      fields: [
        {
          name: 'contactName',
          type: 'text',
          label: 'Contact Name',
          placeholder: '-----',
        },
        {
          name: 'contactEmail',
          type: 'email',
          label: 'Contact Email',
          placeholder: '-----',
        },
      ],
    },
    {
      type: 'row',
      fields: [
        {
          name: 'contactPhone',
          type: 'tel',
          label: 'Contact Phone',
          placeholder: '-----',
        },
        {
          name: 'additionalTo',
          type: 'multiselect-email',
          label: 'Additional To',
          placeholder: '-----',
          separator: ';',
        },
      ],
    },
    {
      type: 'row',
      fields: [
        // {
        //   name: 'additionalToCC',
        //   type: 'email',
        //   label: 'Additional To',
        //   placeholder: '-----',
        // },
        {
          name: 'supportLead',
          type: 'multiselect-email',
          label: 'Additional CC',
          placeholder: '-----',
          separator: ';',
        },
      ],
    },
  ]

  const resolutionFields: Field[] = [
    {
      type: 'row',
      fields: [
        {
          name: 'resolutionDate',
          type: 'date',
          label: 'Resolution Date',
        },
        {
          name: 'statusReasonCode',
          type: 'select',
          label: 'Resolution Reason',
          placeholder: 'Select Resolution Code',
          options: [],
        },
      ],
    },
    {
      name: 'resolutionDescription',
      type: 'textarea',
      label: 'Resolution Description',
      placeholder: 'Enter details about the resolution...',
      rows: 3,
    },
  ]

  const handleTicketSubmit = (data: FormData) => {
    console.log('Ticket Form Data:', data)
    console.log('Form submission working!')
    alert('Form submitted! Check console for data.')
  }

  // Combine all fields into a single array for one form instance
  const allFields: Field[] = [
    ...generalFields,
    ...contactFields,
    ...resolutionFields,
  ]

  const sections = [
    { id: 'general', label: 'General Information', fields: generalFields },
    { id: 'contact', label: 'Contact Information', fields: contactFields },
    {
      id: 'resolution',
      label: 'Resolution Information',
      fields: resolutionFields,
    },
  ]

  const mapCaseToFormValues = (caseObj?: Case): Partial<FormData> => {
    if (!caseObj) return {}

    return {
      description: caseObj.Description,
      status: caseObj.Status,
      statusReason: caseObj.CAD_Status_Reason__c,
      dateOpened: caseObj.CreatedDate,
      dateClosed: caseObj.ClosedDate,
      primaryService: caseObj.Primary_Service__c,
      provider: caseObj.Provider__c,

      contactName: caseObj.Acc_Name_Text__c,
      contactEmail: caseObj.ContactEmail,
      contactPhone: caseObj.ContactPhone,
      additionalTo: caseObj.E2CP__AdditionalTo__c,
      additionalToCC: caseObj.E2CP__AdditionalTo__c,
      supportLead: caseObj.E2CP__AdditionalCC__c,

      resolutionDate: caseObj.Resolution_Date__c,
      statusReasonCode: caseObj.CAD_Resolution_Code__c,
      resolutionDescription: caseObj.citsm__Resolution_Description__c,
    }
  }

  const defaultValues = React.useMemo(
    () => mapCaseToFormValues(casesObj),
    [casesObj],
  )

  useEffect(() => {
    if (casesObj && Object.keys(casesObj).length === 0) {
      toast.error('No cases found')
    }
  }, [casesObj])

  // console.log('defaultValuesfg:', defaultValues)

  return (
    <div className=''>
      <DynamicForm
        fields={allFields}
        onSubmit={handleTicketSubmit}
        defaultValues={defaultValues}
        formRef={formRef}
        sections={sections.map(section => ({
          ...section,
          collapsed: collapsedSections[section.id],
          onToggle: toggleSection,
        }))}
        isViewOnly={isViewOnly}
      />

      {/* <div className='mt-8 flex gap-4 justify-end'>
        <button
          type='button'
          onClick={() => {
            console.log('Submit button clicked')
            console.log('formRef.current:', formRef.current)
            formRef.current?.submit()
          }}
          className='bg-secondary text-primary-foreground py-2 px-6 rounded-md hover:bg-secondary-dark transition-colors'
        >
          Submit Ticket
        </button>
        <button
          type='button'
          className='bg-gray-200 text-gray-700 py-2 px-6 rounded-md hover:bg-gray-300 transition-colors'
        >
          Cancel
        </button>
      </div> */}
    </div>
  )
}

export default CaseDetailsForm
